import { sourceTypeText } from "../../shared/sources";
import { ArrowLeftRight, Check, ChevronDown, FileText, Info, Package, Scale, TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { FINDING_RULES, type FindingDetailResponse, type FindingItem, type FindingRule, type FindingStatus, type FindingsResponse, type Role, type RunItemsResponse, type RunResponse, type Severity } from "../../shared/api";
import { formatAuditTime } from "../../shared/audit";
import { findingStatusLabel, kindLabel, runSummaryLine, severityLabel } from "../../shared/findings";
import { PERMISSIONS } from "../../shared/permissions";
import { formatKg, formatNumber, formatUsd, parseRecordKey } from "../../shared/versions";
import { SeverityBadge, StatusBadge } from "@/components/StatusBadge";
import { Pager } from "@/components/Pager";
import { ErrorAlert } from "@/components/ErrorAlert";
import { PageHeader } from "@/components/PageHeader";
import { StatCard } from "@/components/StatCard";
import { UserAvatar } from "@/components/UserAvatar";
import { SegmentBar } from "@/components/charts";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { addNote, errorText, getFinding, getRunItems, getRunState, runReconciliation, searchFindings } from "@/lib/client";
import { navigate, replaceQuery, uploadHref } from "@/lib/nav";
import { historyHref, recordIdText, recordLinkLabel } from "@/components/UploadDetailScreen";
import { fieldLabel } from "@/lib/upload";
import { CardSkeleton, TableSkeleton } from "@/components/Loading";
import { pathOf, screens } from "../screens";

const LINK = "rounded-sm text-primary underline-offset-2 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring";

const TH = "bg-muted/60 text-xs font-medium text-muted-foreground first:pl-4 last:pr-4";
const TD = "first:pl-4 last:pr-4";

/** Koşu meta satırı (PageHeader altında): koşu no, zaman, kişi, açılan ve kapanan bulgu sayısı. */
export function RunMeta({ run, running }: { run: RunResponse | null; running?: boolean }) {
  return (
    <span role="status" className="inline-flex flex-wrap items-center gap-2">
      {run && <>
        <UserAvatar name={run.runBy} />
        <span>Koşu #{run.runId} · {formatAuditTime(run.startedAt)} · {run.runBy} · {formatNumber(run.openedCount)} bulgu açıldı, {formatNumber(run.closedCount)} bulgu kapandı</span>
      </>}
      {running && <span id="run-running" className="font-medium text-foreground">Mutabakat çalışıyor…</span>}
    </span>
  );
}

/** Dört özet kartı + üst satır (spec §4) + atlanan kurallar. Sayılar API'den (RunSummary); metin tek kaynaktan (shared/findings.ts). */
export function RunBar({ run }: { run: RunResponse | null }) {
  if (!run) return <p className="rounded-xl border bg-card p-4 text-sm text-muted-foreground shadow-xs">Henüz mutabakat çalıştırılmadı.</p>;
  const s = run.summary;
  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard title="Beyanname kalemi" icon={FileText} value={<span className="text-xl">{formatNumber(s.importItems)} ithalat · {formatNumber(s.exportItems)} ihracat</span>} />
        <StatCard title="Stok hareketi" icon={Package} value={formatNumber(s.stockMovements)} />
        <StatCard title="Eşleşen / Eşleşmeyen" icon={ArrowLeftRight} value={`${formatNumber(s.matchedCount)} / ${formatNumber(s.unmatchedCount)}`}>
          <SegmentBar segments={[{ label: "Eşleşen", count: s.matchedCount, color: "primary" }, { label: "Eşleşmeyen", count: s.unmatchedCount, color: "critical" }]} />
        </StatCard>
        <StatCard title="Toplam fark" icon={Scale} value={`${formatKg(s.totalDiffKg)} kg`} />
      </div>
      <p className="text-xs text-muted-foreground">{runSummaryLine(s)}</p>
      {s.skippedRules.map((k) => (
        <Alert key={k.rule} role="note" className="border-warning-border bg-warning text-warning-foreground">
          <TriangleAlert aria-hidden="true" />
          <AlertDescription className="text-warning-foreground">Kural {k.rule} çalışmadı: yüklenmemiş kaynak ({k.missing.map(sourceTypeText).join(", ")}).</AlertDescription>
        </Alert>
      ))}
    </div>
  );
}

const sameGroup = <span className="text-muted-foreground">aynı grup</span>;

/** Beyanname kalemleri, API'den sayfa sayfa (ilk açılışta yüklenir). Gruplu kalemlerde ERP ve fark yalnız ilk kalemde gelir; null "aynı grup" diye gösterilir, 0 diye değil. */
export function ItemsTable({ count, filtered, page, onPage, onOpen, data, error, busy }: {
  /** süzgeçsiz kalem sayısı */ count: number; filtered: boolean; page: number; onPage: (p: number) => void; onOpen: () => void; data: RunItemsResponse | null; error?: string; busy?: boolean;
}) {
  return (
    <details className="group overflow-clip rounded-xl border bg-card text-sm text-card-foreground shadow-xs" onToggle={(e) => { if (e.currentTarget.open) onOpen(); }}>
      <summary className="flex list-none items-center justify-between rounded-xl px-4 py-3 text-base font-semibold outline-none focus-visible:ring-[3px] focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        Beyanname kalemleri ({filtered && data ? `${formatNumber(count)} kalemden ${formatNumber(data.total)} tanesi` : formatNumber(count)})
        <ChevronDown aria-hidden="true" className="size-4 text-muted-foreground transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t transition-opacity aria-busy:opacity-50" aria-busy={busy}>
      {error && <div className="p-4"><ErrorAlert>{error}</ErrorAlert></div>}
      {!data && !error && <TableSkeleton />}
      {data && <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className={TH}>Beyanname</TableHead><TableHead className={TH}>Kalem</TableHead>
            <TableHead className={`${TH} text-right`}>Beyan (kg)</TableHead><TableHead className={`${TH} text-right`}>ERP (kg)</TableHead>
            <TableHead className={`${TH} text-right`}>Fark (kg)</TableHead><TableHead className={TH}>Durum</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.items.map((i) => (
            <TableRow key={`${i.rule}|${i.declaration}|${i.itemNo}`}>
              <TableCell className={`${TD} font-mono text-xs`}>{i.declaration}</TableCell><TableCell className={TD}>{i.itemNo}</TableCell>
              <TableCell className={`${TD} text-right tabular-nums`}>{formatKg(i.declaredKg)}</TableCell>
              <TableCell className={`${TD} text-right tabular-nums`}>{i.erpKg === null ? sameGroup : formatKg(i.erpKg)}</TableCell>
              <TableCell className={`${TD} text-right tabular-nums`}>{i.diffKg === null ? sameGroup : formatKg(i.diffKg)}</TableCell>
              <TableCell className={`${TD} ${i.matched ? "" : "font-medium text-critical-foreground"}`}>{i.matched ? "Eşleşti" : "Eşleşmedi"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.total > data.pageSize && <div className="border-t px-4 py-2"><Pager label="Kalem sayfaları" page={page} total={data.total} pageSize={data.pageSize} onPage={onPage} /></div>}
      </>}
      </div>
    </details>
  );
}

/** E3 süzgeci: şiddet (çoklu), kural, arama. Adreste `?severity=critical,warning&rule=R1&q=`; değerler API'ye olduğu gibi gider. */
export type FindingFilter = { severity: Severity[]; rule: FindingRule | ""; q: string };
const SEVERITIES: Severity[] = ["critical", "warning", "info"];
export function readFilter(search: string): FindingFilter {
  const u = new URLSearchParams(search);
  const rule = u.get("rule") ?? "";
  return {
    severity: SEVERITIES.filter((v) => (u.get("severity") ?? "").split(",").includes(v)),
    rule: (FINDING_RULES as readonly string[]).includes(rule) ? (rule as FindingRule) : "",
    q: (u.get("q") ?? "").slice(0, 200),
  };
}
const filterActive = (f: FindingFilter) => f.severity.length > 0 || f.rule !== "" || f.q.trim() !== "";

export function FindingFilters({ filter, onChange }: { filter: FindingFilter; onChange: (f: FindingFilter) => void }) {
  const [text, setText] = useState(filter.q);
  useEffect(() => setText(filter.q), [filter.q]);
  const search = (e: FormEvent) => { e.preventDefault(); onChange({ ...filter, q: text.trim() }); };
  return (
    <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
      <div role="group" aria-label="Şiddet" className="flex flex-col gap-2">
        <span className="text-sm font-medium">Şiddet</span>
        <div className="flex gap-1">
          {SEVERITIES.map((v) => {
            const on = filter.severity.includes(v);
            return (
              <Button key={v} type="button" size="sm" variant="outline" aria-pressed={on} className={on ? "border-foreground/30 bg-secondary" : undefined}
                onClick={() => onChange({ ...filter, severity: on ? filter.severity.filter((x) => x !== v) : [...filter.severity, v] })}>{on && <Check aria-hidden="true" className="size-3.5" />}{severityLabel[v]}</Button>
            );
          })}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="finding-rule">Kural</Label>
        <NativeSelect id="finding-rule" size="sm" value={filter.rule} onChange={(e) => onChange({ ...filter, rule: e.target.value as FindingRule | "" })}>
          <option value="">Tüm kurallar</option>
          {FINDING_RULES.map((r) => <option key={r} value={r}>Kural {r}</option>)}
        </NativeSelect>
      </div>
      <form role="search" onSubmit={search} className="flex items-end gap-2">
        <div className="flex flex-col gap-2">
          <Label htmlFor="finding-q">Beyanname ya da kayıt kimliğinde ara</Label>
          <Input id="finding-q" className="w-64" value={text} onChange={(e) => setText(e.target.value)} maxLength={200} />
        </div>
        <Button type="submit" variant="outline" size="sm">Ara</Button>
        {filter.q && <Button type="button" variant="outline" size="sm" onClick={() => onChange({ ...filter, q: "" })}>Temizle</Button>}
      </form>
      {filterActive(filter) && <Button type="button" variant="outline" size="sm" onClick={() => onChange({ severity: [], rule: "", q: "" })}>Süzgeci temizle</Button>}
    </div>
  );
}

/** Şiddet sıralamasını API verir (critical, warning, info); istemci yeniden sıralamaz. Tablo kartı (ui.md §12.10). */
export function FindingsTable({ items, selectedId, onSelect, footer }: { items: FindingItem[]; selectedId: number | null; onSelect: (id: number) => void; footer?: ReactNode }) {
  return (
    <Card className="gap-0 overflow-clip p-0">
      <Table>
        <TableHeader>
          <TableRow><TableHead className={TH}>Şiddet</TableHead><TableHead className={TH}>Bulgu</TableHead><TableHead className={TH}>Açılış</TableHead><TableHead className={TH}>Durum</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 && <TableRow><TableCell colSpan={4} className={`${TD} text-muted-foreground`}>Bu filtrede kayıt yok.</TableCell></TableRow>}
          {items.map((f) => (
            <TableRow key={f.findingId} data-state={f.findingId === selectedId ? "selected" : undefined}>
              <TableCell className={TD}><SeverityBadge severity={f.severity} /></TableCell>
              <TableCell className={`${TD} whitespace-normal`}>
                <button type="button" className="rounded-sm text-left text-primary underline-offset-2 outline-none hover:underline focus-visible:ring-[3px] focus-visible:ring-ring" aria-pressed={f.findingId === selectedId} data-finding-id={f.findingId} onClick={() => onSelect(f.findingId)}>
                  {f.kind ? <b>{kindLabel[f.kind]}: </b> : null}{f.title}
                </button>
              </TableCell>
              <TableCell className={`${TD} tabular-nums text-muted-foreground`}>{formatAuditTime(f.openedAt).slice(0, 10)}</TableCell>
              <TableCell className={`${TD} whitespace-normal`}>{f.status === "closed" ? f.closeText ?? findingStatusLabel.closed : findingStatusLabel.open}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {footer && <div className="border-t px-4 py-2">{footer}</div>}
    </Card>
  );
}

/** Sağ panel: ilgili kayıtlar (dosya + satır), hesap adımları, olgu tutarları, sabit cümleler, notlar. Para olarak yalnız olgu. */
export function FindingPanel({ d, canWrite, note, noteBusy, onNote, onNoteChange, onClose }: {
  d: FindingDetailResponse; canWrite: boolean; note: string; noteBusy: boolean; onNote: () => void; onNoteChange: (v: string) => void; onClose?: () => void;
}) {
  // Beyanname içeren bulgudan E4'e: ref = beyanname no (kayıt kimliğinin ilk parçası)
  const decl = d.records.find((r) => r.sourceType === "GUMRUK_ITH" || r.sourceType === "GUMRUK_IHR");
  const traceHref = decl ? `${pathOf("E4")}?ref=${encodeURIComponent(parseRecordKey(decl.recordKey)[0] ?? "")}` : null;
  // DATA_* başlığı hangi kaydın değiştiğini yazmaz; kimlik kayıttan gelir
  const dataRec = !d.kind && d.records[0] ? d.records[0] : null;
  const facts: [string, string][] = [];
  if (d.quantity !== undefined) facts.push(["Miktar", `${d.unit === "KG" ? formatKg(d.quantity) : formatNumber(d.quantity)}${d.unit ? ` ${d.unit.toLowerCase()}` : ""}`]);
  if (d.valueUsd !== undefined) facts.push(["Kıymet", `${formatUsd(d.valueUsd)} USD`]);
  if (d.importedInputEquivalent) {
    facts.push(["İthal girdi karşılığı", `${formatKg(d.importedInputEquivalent.importedInputKg)} kg · ${formatUsd(d.importedInputEquivalent.importedInputValueUsd)} USD`]);
  }
  const submit = (e: FormEvent) => { e.preventDefault(); onNote(); };
  return (
    <aside aria-label="Bulgu ayrıntısı" className="flex min-w-0 flex-col gap-4 rounded-xl border bg-card p-4 text-sm text-card-foreground shadow-xs xl:sticky xl:top-4 xl:max-h-[calc(100svh-6rem)] xl:overflow-y-auto">
      <header className="flex flex-col gap-2">
        <p className="flex flex-wrap items-center gap-2">
          <SeverityBadge severity={d.severity} />
          <span className="text-xs text-muted-foreground">Kural {d.rule}</span>
          <span className="text-xs text-muted-foreground">· {d.status === "closed" ? "Kapandı" : "Açık"}</span>
          {onClose && <Button type="button" variant="ghost" size="icon-sm" className="ml-auto" aria-label="Paneli kapat" onClick={onClose}><X aria-hidden="true" /></Button>}
        </p>
        <h2 className="text-lg font-semibold">{d.kind ? <b>{kindLabel[d.kind]}: </b> : null}{dataRec ? <><b>{d.title}: </b>{recordIdText(dataRec.sourceType, dataRec.recordKey)}</> : d.title}</h2>
        {traceHref && <a href={traceHref} onClick={(e) => navigate(e, traceHref)} className={`self-start text-sm ${LINK}`}>İzle →</a>}
        {d.closeText && <p>{d.closeText}</p>}
      </header>
      {d.text.map((t) => <p key={t} className="flex gap-2 rounded-lg bg-muted p-3"><Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-muted-foreground" /><span>{t}</span></p>)}
      {facts.length > 0 && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
          {facts.map(([k, v]) => <div key={k} className="contents"><dt className="text-muted-foreground">{k}</dt><dd className="tabular-nums">{v}</dd></div>)}
        </dl>
      )}
      {d.steps.length > 0 && (
        <section aria-label="Hesap adımları"><h3 className="mb-2 font-medium">Hesap adımları</h3>
          <ol className="flex flex-col gap-2">{d.steps.map((s, i) => <li key={i} className="flex items-start gap-2"><span aria-hidden="true" className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-xs">{i + 1}</span><span>{s}</span></li>)}</ol>
        </section>
      )}
      {d.fields.length > 0 && (
        <section aria-label="Alan farkı"><h3 className="font-medium">Alan farkı</h3>
          <ul className="font-mono text-xs">{d.fields.map((f) => <li key={f.field}>{fieldLabel(f.field)}: {String(f.old ?? "yok")} → {String(f.new ?? "yok")}</li>)}</ul>
        </section>
      )}
      <section aria-label="İlgili kayıtlar"><h3 className="font-medium">İlgili kayıtlar</h3>
        <ul className="flex flex-col gap-1">{d.records.map((r) => {
          const up = `${uploadHref(r.uploadId)}?keyContains=${encodeURIComponent(parseRecordKey(r.recordKey)[0] ?? "")}`, hist = historyHref(r.sourceType, r.recordKey);
          return (
            <li key={`${r.sourceType}|${r.uploadId}|${r.recordKey}`}>
              <a href={up} onClick={(e) => navigate(e, up)} className={LINK}>{r.fileName}{r.rowNo !== null ? `, satır ${formatNumber(r.rowNo)}` : ""}</a>
              {" · "}<a href={hist} aria-label={recordLinkLabel(r.sourceType, r.recordKey)} onClick={(e) => navigate(e, hist)} className={LINK}>{recordIdText(r.sourceType, r.recordKey)}</a>
              {r.manualEntryId !== null && <StatusBadge tone="info" className="ml-2">Elle girildi</StatusBadge>}
            </li>
          );
        })}</ul>
      </section>
      <section aria-label="Notlar"><h3 className="font-medium">Notlar</h3>
        {d.notes.length === 0 ? <p className="text-muted-foreground">Not yok.</p> : (
          <ul className="flex flex-col gap-3">{d.notes.map((n) => (
            <li key={n.noteId} className="flex items-start gap-2">
              <UserAvatar name={n.author} />
              <span className="min-w-0"><span className="text-muted-foreground">{n.author} · {formatAuditTime(n.createdAt)}</span><br />{n.body}</span>
            </li>
          ))}</ul>
        )}
        {canWrite && (
          <form onSubmit={submit} className="mt-2 flex flex-col gap-2">
            <Label htmlFor="finding-note">Not ekle</Label>
            <Textarea id="finding-note" value={note} onChange={(e) => onNoteChange(e.target.value)} maxLength={4000} />
            <Button type="submit" size="sm" variant="outline" className="self-start" disabled={noteBusy || !note.trim()}>Notu kaydet</Button>
          </form>
        )}
      </section>
    </aside>
  );
}

const RUN_POLL_MS = 3000;

/** E3 (spec §6). `role` yalnız Çalıştır ve not alanını gösterip göstermemeye karar verir; yetkiyi API denetler (ADR-0008). */
export function ReconciliationScreen({ role }: { role: Role }) {
  const canWrite = (PERMISSIONS.write as readonly Role[]).includes(role);
  const [run, setRun] = useState<RunResponse | null>(null);
  const [runLoaded, setRunLoaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<FindingStatus>("open");
  const [page, setPage] = useState(1);
  const [filter, setFilterState] = useState<FindingFilter>(() => readFilter(typeof window === "undefined" ? "" : window.location.search));
  const [listKey, setListKey] = useState("");
  const [itemsOpen, setItemsOpen] = useState(false);
  const [itemsPage, setItemsPage] = useState(1);
  const [items, setItems] = useState<RunItemsResponse | null>(null);
  const [itemsError, setItemsError] = useState("");
  const [itemsBusy, setItemsBusy] = useState(false);
  const [list, setList] = useState<FindingsResponse | null>(null);
  const [detail, setDetail] = useState<FindingDetailResponse | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const statusRef = useRef(status); statusRef.current = status;
  const [counts, setCounts] = useState<Partial<Record<FindingStatus, number>>>({});

  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => { if (detail) panelRef.current?.scrollIntoView?.({ block: "nearest" }); }, [detail]); // xl altında panel tablonun altına iner
  const latest = useRef(0); // yalnız son isteğin yanıtı yazılır; geç gelen eski yanıt yok sayılır
  const keyOf = (st: FindingStatus, p: number, f: FindingFilter) => `${st}|${p}|${f.severity}|${f.rule}|${f.q}`;
  const loadList = useCallback((st: FindingStatus, p: number, f: FindingFilter) => {
    const id = ++latest.current, key = keyOf(st, p, f);
    return searchFindings({ status: st, page: p, severity: f.severity, rule: f.rule ? [f.rule] : undefined, q: f.q }).then(
      (l) => { if (id === latest.current) { setList(l); setListKey(key); if (!filterActive(f)) setCounts((c) => ({ ...c, [st]: l.total })); } }, // sekme sayısı filtresiz toplam
      (e) => { if (id === latest.current) setListKey(key); throw e; }, // hatada soluk kalkar
    );
  }, []);
  const setFilter = (f: FindingFilter) => {
    setFilterState(f); setPage(1); if (f.q !== filter.q) setItemsPage(1);
    replaceQuery("severity", f.severity.join(",") || null); replaceQuery("rule", f.rule || null); replaceQuery("q", f.q || null);
  };
  const open = useCallback((id: number) => {
    setSelected(id); setNote(""); replaceQuery("finding", id);
    return getFinding(id).then(setDetail);
  }, []);
  useEffect(() => { // ?finding=<id> (E1 link): open that finding's panel
    const id = Number(new URLSearchParams(window.location.search).get("finding"));
    if (Number.isInteger(id) && id > 0) void open(id).catch((e) => setError(errorText(e)));
  }, [open]);
  useEffect(() => { getRunState().then((s) => { setRun(s.run); setRunning(s.running); }).catch((e) => setError(errorText(e))).finally(() => setRunLoaded(true)); }, []);
  useEffect(() => { // koşu kilidi tutuluyken birkaç saniyede bir yokla; false olunca sonucu yükle, dur
    if (!running) return;
    let fails = 0;
    const t = setInterval(() => {
      getRunState().then((s) => {
        fails = 0;
        if (!s.running) { setRun(s.run); setRunning(false); setPage(1); void loadList(statusRef.current, 1, filterRef.current).catch(() => {}); }
      }, (e) => { if (++fails >= 3) { setRunning(false); setError(errorText(e)); } }); // üç hatada dur, düğme açılsın
    }, RUN_POLL_MS);
    return () => clearInterval(t);
  }, [running, loadList]);
  useEffect(() => { loadList(status, page, filter).catch((e) => setError(errorText(e))); }, [status, page, filter, loadList]);
  const filterRef = useRef(filter); filterRef.current = filter;
  useEffect(() => { // sekme sayıları ("Açık (N)", "Kapandı (N)"): filtresiz, yalnız API'nin total'i; açık sekme filtresizse listeden gelir
    for (const s of ["open", "closed"] as const) {
      if (s === statusRef.current && !filterActive(filterRef.current)) continue;
      searchFindings({ status: s }).then((l) => setCounts((c) => ({ ...c, [s]: l.total })), () => {});
    }
  }, [run]);
  useEffect(() => {
    if (!itemsOpen || !run) return;
    setItemsError(""); setItemsBusy(true);
    getRunItems(filter.q, itemsPage).then(setItems, (e) => setItemsError(errorText(e))).finally(() => setItemsBusy(false));
  }, [itemsOpen, itemsPage, run, filter.q]);
  const close = useCallback(() => {
    setDetail(null); setSelected(null); setNote(""); replaceQuery("finding", null);
    const id = selected;
    queueMicrotask(() => {
      const row = document.querySelector<HTMLElement>(`[data-finding-id="${id}"]`);
      const h1 = document.querySelector<HTMLElement>("h1"); // satır bu sayfada yoksa (?finding= bağlantısı)
      if (row) row.focus(); else if (h1) { h1.tabIndex = -1; h1.focus(); }
    });
  }, [selected]);
  useEffect(() => {
    if (!detail) return;
    // alan içindeyken Esc yazılan notu silmesin
    const on = (e: KeyboardEvent) => { if (e.key === "Escape" && !e.defaultPrevented && !(e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement)) close(); };
    document.addEventListener("keydown", on);
    return () => document.removeEventListener("keydown", on);
  }, [detail, close]);

  const guard = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  const start = () => guard(async () => {
    setRun(await runReconciliation());
    setPage(1);
    await loadList(status, 1, filter);
    if (selected !== null) await open(selected);
  });
  const saveNote = () => guard(async () => {
    await addNote(selected!, note.trim());
    await open(selected!);
  });

  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader title="Mutabakat" description={screens.find((x) => x.id === "E3")!.description} meta={runLoaded ? <RunMeta run={run} running={running} /> : undefined}
        actions={canWrite ? <Button onClick={() => void start()} disabled={busy || running} aria-describedby={running ? "run-running" : undefined}>{busy ? "Çalışıyor…" : "Çalıştır"}</Button> : undefined} />
      {runLoaded ? <RunBar run={run} /> : <CardSkeleton />}
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {run && <ItemsTable count={run.summary.items.length} filtered={filter.q !== ""} page={itemsPage} onPage={setItemsPage} onOpen={() => setItemsOpen(true)} data={items} error={itemsError} busy={itemsBusy} />}
      {runLoaded && !run ? null : <>
      <div role="group" aria-label="Bulgu durumu" className="inline-flex w-fit gap-1 rounded-lg bg-muted p-1">
        {(["open", "closed"] as const).map((s) => (
          <Button key={s} size="sm" variant="ghost" className={status === s ? "bg-card shadow-xs hover:bg-card" : "text-muted-foreground"} aria-pressed={status === s} onClick={() => { setStatus(s); setPage(1); setSelected(null); setDetail(null); replaceQuery("finding", null); }}>
            {findingStatusLabel[s]}{counts[s] !== undefined ? ` (${formatNumber(counts[s]!)})` : ""}
          </Button>
        ))}
      </div>
      <FindingFilters filter={filter} onChange={setFilter} />
      <div className={`grid items-start gap-6 ${detail ? "xl:grid-cols-[minmax(0,1fr)_26rem]" : ""}`}>
        {list ? (
          <div className="min-w-0 transition-opacity aria-busy:opacity-50" aria-busy={listKey !== keyOf(status, page, filter)}>
            <FindingsTable items={list.items} selectedId={selected} onSelect={(id) => void guard(() => open(id))}
              footer={list.total > list.pageSize ? <Pager label="Bulgu sayfaları" page={list.page} total={list.total} pageSize={list.pageSize} onPage={setPage} /> : undefined} />
            <p role="status" className="mt-2 min-h-5 text-sm text-muted-foreground">{filterActive(filter) ? `${formatNumber(list.total)} bulgu gösteriliyor` : ""}</p>
          </div>
        ) : <TableSkeleton />}
        {detail && <div ref={panelRef} className="min-w-0"><FindingPanel d={detail} canWrite={canWrite} note={note} noteBusy={busy} onNote={() => void saveNote()} onNoteChange={setNote} onClose={close} /></div>}
      </div>
      </>}
    </main>
  );
}
